/**
 * secret-redaction —— 「这段文本里有没有一把密钥 / 口令」的运行时判据与打码（B-203）。
 *
 * ## 业务白话
 * 员工会把服务器 root 密码、应用 Secret、群机器人地址直接写进对话、脚本或定时任务；AI 读到后
 * 又抄进新脚本，整段对话再随会话记录上传企业后端。这里把「看起来是一把真密钥」的那一段认出来，
 * 换成 `[已隐藏的密码]` 这类标签：进模型前、上传会话记录前、写文件前三个口共用这一份判据。
 *
 * ## 单一判据
 * 形状表只有一份：同目录 `secret-shapes.json`，提交期明文凭证门
 * （scripts/lib/plaintext-credential-scan.mjs）读同一张表。本文件只负责两件运行时专属的事：
 *   1. 每条形状的 `runtimeCheck`（值像不像口令）——提交期那边换成 `isLikelyRealSecret`（夹具必须长得假），
 *      运行时不能用它：真口令常常就是 `Abc@2024` 这种「人手编」的样子，按合成否决会整类漏掉。
 *   2. 打码标签与幂等：已经是标签的值不再被识别，打两遍与打一遍结果相同。
 *
 * ## 高置信优先（误报的代价）
 * 误报 = 模型读不到一段本该读到的文字、或一次正常的写文件被拦。故只认三类：带厂商前缀的密钥、
 * 机器人地址里的密钥段、「密钥名 + 赋值 / 命令行口令参数 / expect 的口令提示后的 send / SSH_ASKPASS
 * 辅助脚本的 echo」上下文里的值。裸键名 `token` 与 `SPREADSHEET_TOKEN` 一类文档编号键名（`keyChecks`）、
 * 飞书编号前缀的值（`valueChecks.documentIds`）、git 提交号、UUID、变量名、路径、占位符一律不认——
 * 误报面由 secret-redaction.test.ts 的绿对照钉住。
 *
 * Pure + dependency-free：core（进模型前、写文件门、会话记录上传）与 ui 都可以直接用。
 */
import shapesJson from "./secret-shapes.json" with { type: "json" };
/** 已知真值最短多长才按真值替换。 */
export const MIN_KNOWN_SECRET_LENGTH = 8;
const KIND_LABEL = {
    apiKey: "密钥",
    token: "令牌",
    password: "密码",
    privateKey: "私钥",
    webhook: "机器人密钥",
    platformCredential: "平台凭证",
};
/** 打码后模型看到的标签。写文件门靠它认出「AI 把被隐藏的值抄进了文件」。 */
export function secretRedactionMarker(kind) {
    return `[已隐藏的${KIND_LABEL[kind]}]`;
}
/** 文本里是否带着本模块打出的标签。 */
export const SECRET_REDACTION_MARKER_RE = /\[已隐藏的(?:密钥|令牌|密码|私钥|机器人密钥|平台凭证)\]/;
/** 形状表里允许出现的 kind（`platformCredential` 只由调用方按真值点名产生，不写进表）。 */
const SECRET_KINDS = new Set(["apiKey", "token", "password", "privateKey", "webhook"]);
const RUNTIME_CHECKS = new Set(["none", "secretLike", "relaxed"]);
function isStringArray(v) {
    return Array.isArray(v) && v.every((x) => typeof x === "string");
}
/**
 * 读形状表并编译运行时那一半。表是随包发的常量，结构不对就是构建缺陷：直接抛，
 * 不静默跳过某一条（跳过 = 那一类密钥悄悄不打码）。
 */
function compileRuntimeShapes(raw) {
    const shapes = raw?.shapes;
    if (!Array.isArray(shapes))
        throw new Error("secret-shapes.json: shapes 不是数组");
    const out = [];
    const seen = new Set();
    for (const entry of shapes) {
        if (!entry || typeof entry !== "object")
            throw new Error("secret-shapes.json: 条目不是对象");
        const e = entry;
        const { id, kind, pattern, flags, keywords, runtimeCheck, surfaces, trimTrailing } = e;
        if (typeof id !== "string" || !id)
            throw new Error("secret-shapes.json: 条目缺 id");
        if (seen.has(id))
            throw new Error(`secret-shapes.json: id 重复 ${id}`);
        seen.add(id);
        if (typeof kind !== "string" || !SECRET_KINDS.has(kind))
            throw new Error(`secret-shapes.json: ${id} kind 非法`);
        if (typeof pattern !== "string" || typeof flags !== "string")
            throw new Error(`secret-shapes.json: ${id} pattern/flags 非法`);
        if (!isStringArray(keywords) || !isStringArray(surfaces))
            throw new Error(`secret-shapes.json: ${id} keywords/surfaces 非法`);
        if (typeof runtimeCheck !== "string" || !RUNTIME_CHECKS.has(runtimeCheck)) {
            throw new Error(`secret-shapes.json: ${id} runtimeCheck 非法`);
        }
        if (trimTrailing !== undefined && typeof trimTrailing !== "string") {
            throw new Error(`secret-shapes.json: ${id} trimTrailing 非法`);
        }
        if (!surfaces.includes("runtime"))
            continue;
        out.push({
            id,
            kind: kind,
            re: new RegExp(pattern, `${flags}gd`),
            keywords: keywords.map((k) => k.toLowerCase()),
            check: runtimeCheck,
            trimTrailing: trimTrailing ?? "",
        });
    }
    return out;
}
const RUNTIME_SHAPES = compileRuntimeShapes(shapesJson);
/** 运行时生效的形状 id（测试与诊断用）。 */
export const RUNTIME_SECRET_SHAPE_IDS = Object.freeze(RUNTIME_SHAPES.map((s) => s.id));
function compilePatternList(raw, where) {
    if (!Array.isArray(raw))
        throw new Error(`secret-shapes.json: ${where} 不是数组`);
    return raw.map((p) => {
        const e = p;
        if (!e || typeof e.pattern !== "string" || typeof e.flags !== "string") {
            throw new Error(`secret-shapes.json: ${where} 条目缺 pattern / flags`);
        }
        return new RegExp(e.pattern, e.flags);
    });
}
function compileValueChecks(raw) {
    const vc = raw?.valueChecks;
    if (!vc)
        throw new Error("secret-shapes.json: 缺 valueChecks");
    const sl = vc.secretLike;
    const rl = vc.relaxed;
    if (!sl || typeof sl.minLength !== "number" || !rl || typeof rl.minLength !== "number") {
        throw new Error("secret-shapes.json: valueChecks.secretLike / relaxed 缺 minLength");
    }
    return {
        placeholders: compilePatternList(vc.placeholders, "valueChecks.placeholders"),
        documentIds: compilePatternList(vc.documentIds, "valueChecks.documentIds"),
        secretLike: {
            minLength: sl.minLength,
            rejectIf: compilePatternList(sl.rejectIf, "valueChecks.secretLike.rejectIf"),
            requireAll: compilePatternList(sl.requireAll, "valueChecks.secretLike.requireAll"),
        },
        relaxed: { minLength: rl.minLength },
    };
}
const VALUE_CHECKS = compileValueChecks(shapesJson);
/** 占位符或飞书文档编号：两档（secretLike / relaxed）都不认。 */
function isKnownNonSecretValue(v) {
    return VALUE_CHECKS.placeholders.some((re) => re.test(v)) || VALUE_CHECKS.documentIds.some((re) => re.test(v));
}
function compileKeyChecks(raw) {
    const kc = raw?.keyChecks;
    if (!kc)
        throw new Error("secret-shapes.json: 缺 keyChecks");
    return { documentIds: compilePatternList(kc.documentIds, "keyChecks.documentIds") };
}
const KEY_CHECKS = compileKeyChecks(shapesJson);
/**
 * 键名是不是文档 / 表格 / 知识库编号的名字（`SPREADSHEET_TOKEN`、`app_token`，B-280）。规则是数据
 * （secret-shapes.json `keyChecks.documentIds`），提交期门读同一份，由 `__fixtures__/secret-value-cases.json`
 * 的 keyCases 对账。只作用于带具名组 `key` 的形状：命中即整条不认、与值无关。
 */
export function isDocumentIdKey(key) {
    return KEY_CHECKS.documentIds.some((re) => re.test(key));
}
/**
 * 「密钥名 / 口令参数」上下文里的值像不像一条口令。规则是数据（secret-shapes.json
 * `valueChecks.secretLike`），提交期门读同一份；这里与 scripts 那边各有一个解释器，
 * 由 `__fixtures__/secret-value-cases.json` 对账。
 * 认：够长、含字母、不是占位符；不认：变量名 / 常量名 / 类型注解、纯数字、点分标识符与调用表达式、
 * 路径、URL、已打码的标签。宁可漏「HelloWorld」这种纯字母口令，也不把代码当密钥。
 */
export function isSecretLikeValue(v) {
    const c = VALUE_CHECKS.secretLike;
    if (v.length < c.minLength || isKnownNonSecretValue(v))
        return false;
    if (c.rejectIf.some((re) => re.test(v)))
        return false;
    return c.requireAll.every((re) => re.test(v));
}
export function isRelaxedSecretValue(v) {
    return v.length >= VALUE_CHECKS.relaxed.minLength && !isKnownNonSecretValue(v);
}
function passes(check, value) {
    if (SECRET_REDACTION_MARKER_RE.test(value))
        return false;
    if (check === "none")
        return true;
    if (check === "relaxed")
        return isRelaxedSecretValue(value);
    return isSecretLikeValue(value);
}
/**
 * 找出文本里所有该遮掉的段，按起点排序、重叠的合并成一段。
 * 一个值常同时命中「厂商前缀」与「密钥名赋值」两条，合并后只遮一次。
 */
export function findSecretSpans(text, options) {
    if (!text)
        return [];
    const lower = text.toLowerCase();
    const spans = [];
    for (const known of options?.knownValues ?? []) {
        if (typeof known !== "string" || known.length < MIN_KNOWN_SECRET_LENGTH)
            continue;
        for (let at = text.indexOf(known); at !== -1; at = text.indexOf(known, at + known.length)) {
            spans.push({ start: at, end: at + known.length, shapeId: "known-value", kind: "platformCredential" });
        }
    }
    for (const shape of options?.shapes === false ? [] : RUNTIME_SHAPES) {
        if (shape.keywords.length > 0 && !shape.keywords.some((k) => lower.includes(k)))
            continue;
        for (const m of text.matchAll(shape.re)) {
            const groups = m.groups;
            if (groups?.key !== undefined && isDocumentIdKey(groups.key))
                continue;
            const groupIdx = m.indices?.groups;
            const which = groups?.value !== undefined ? "value" : groups?.bare !== undefined ? "bare" : null;
            let value = which && groups ? groups[which] : m[0];
            const range = which && groupIdx ? groupIdx[which] : m.indices?.[0];
            if (value === undefined || !range)
                continue;
            let end = range[1];
            while (value.length > 0 && shape.trimTrailing.includes(value[value.length - 1])) {
                value = value.slice(0, -1);
                end--;
            }
            if (!passes(shape.check, value))
                continue;
            spans.push({ start: range[0], end, shapeId: shape.id, kind: shape.kind });
        }
    }
    if (spans.length < 2)
        return spans;
    spans.sort((a, b) => a.start - b.start || b.end - a.end);
    const merged = [spans[0]];
    for (const s of spans.slice(1)) {
        const last = merged[merged.length - 1];
        if (s.start < last.end) {
            if (s.end > last.end)
                last.end = s.end;
            continue;
        }
        merged.push({ ...s });
    }
    return merged;
}
export function containsSecret(text, options) {
    return findSecretSpans(text, options).length > 0;
}
/** 把每一段换成对应标签。返回的 text 与原文只在被遮的段上不同。 */
export function redactSecrets(text, options) {
    const spans = findSecretSpans(text, options);
    if (spans.length === 0)
        return { text, redactedCount: 0, kinds: [] };
    let out = "";
    let cursor = 0;
    const kinds = [];
    for (const s of spans) {
        out += text.slice(cursor, s.start) + secretRedactionMarker(s.kind);
        cursor = s.end;
        if (!kinds.includes(s.kind))
            kinds.push(s.kind);
    }
    out += text.slice(cursor);
    return { text: out, redactedCount: spans.length, kinds };
}
/**
 * 结构化值逐个字符串打码（工具结果对象、会话记录的一行 JSON）。形状不变：对象键、数组长度、
 * 非字符串叶子原样保留——工具结果被 SDK 按工具的输出形状校验，形状一变就整份被丢回原值。
 * 没有任何命中时返回**同一个引用**，调用方可以用 `===` 判「没动过」。
 */
export function redactSecretsDeep(input, options) {
    let redactedCount = 0;
    const kinds = [];
    const walk = (v, depth) => {
        if (typeof v === "string") {
            const r = redactSecrets(v, options);
            if (r.redactedCount === 0)
                return v;
            redactedCount += r.redactedCount;
            for (const k of r.kinds)
                if (!kinds.includes(k))
                    kinds.push(k);
            return r.text;
        }
        if (depth > 64 || v === null || typeof v !== "object")
            return v;
        if (Array.isArray(v)) {
            let changed = false;
            const next = v.map((item) => {
                const w = walk(item, depth + 1);
                if (w !== item)
                    changed = true;
                return w;
            });
            return changed ? next : v;
        }
        const proto = Object.getPrototypeOf(v);
        if (proto !== Object.prototype && proto !== null)
            return v;
        let changed = false;
        const next = {};
        for (const [k, item] of Object.entries(v)) {
            const w = walk(item, depth + 1);
            if (w !== item)
                changed = true;
            // defineProperty 而不是 `next[k] = w`：JSON.parse 出来的 `__proto__` 是普通自有键，
            // 赋值写法会把它变成改原型、键就丢了。
            Object.defineProperty(next, k, { value: w, enumerable: true, writable: true, configurable: true });
        }
        return changed ? next : v;
    };
    const value = walk(input, 0);
    return { value, redactedCount, kinds };
}
//# sourceMappingURL=secret-redaction.js.map